import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import dayjs from 'dayjs';
import OrderKittingService from 'services/OrderKittingService';
import SettingsService from 'services/SettingsService';
import DeleteRuleDialog from './components/DeleteRuleDialog';
import HistoryTable from './components/HistoryTable';
import ModeDialog from './components/ModeDialog';
import OrderDetailDrawer from './components/OrderDetailDrawer';
import PreviewDialog from './components/PreviewDialog';
import RuleDialog from './components/RuleDialog';
import RulesTable from './components/RulesTable';
import SetupDialog from './components/SetupDialog';
import {
  CredentialsResponse,
  KittingAction,
  KittingRule,
  ORDER_KITTING_NAMESPACE,
  OrderKittingSettings,
  OUTCOMES,
  rulesToStored,
  STORES,
} from './types';

const PAGE_SIZE = 100;
const OVERVIEW = 'overview';
const PRESETS = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 6 },
  { label: 'Last 30 days', days: 29 },
];

// Tab status dot: green = live, yellow = dry-run, gray = paused (disabled).
function storeStatus(settings: OrderKittingSettings | null, store: string) {
  const storeSettings = settings?.stores?.[store];
  if (!settings || !storeSettings) return { label: 'Loading…', color: '#CBD5E1' };
  if (!storeSettings.enabled) return { label: 'Paused', color: '#94A3B8' };
  if (storeSettings.live) return { label: 'Live', color: '#22C55E' };
  return { label: 'Dry-run', color: '#EAB308' };
}

// Order Kitting: when a Dosaze order contains the component SKUs of a rule
// (e.g. 2x of the standard pillow), the backend job replaces those lines with
// the pre-kitted bundle SKU before ShipBob imports the order — one pick and one
// box instead of two. One tab per storefront (that store's rules + history);
// the Overview tab shows kitting history across all stores with filters.
export default function OrderKitting() {
  const service = React.useMemo(() => new OrderKittingService(), []);
  const settingsService = React.useMemo(() => new SettingsService(), []);

  const [tab, setTab] = React.useState<string>(OVERVIEW);
  const [settings, setSettings] = React.useState<OrderKittingSettings | null>(null);
  const [settingsError, setSettingsError] = React.useState<string | null>(null);
  const [savingRules, setSavingRules] = React.useState(false);
  const [modeOpen, setModeOpen] = React.useState(false);
  const [setupOpen, setSetupOpen] = React.useState(false);
  const [credentials, setCredentials] = React.useState<CredentialsResponse | null>(null);
  const [ruleDialogOpen, setRuleDialogOpen] = React.useState(false);
  const [editingRule, setEditingRule] = React.useState<KittingRule | null>(null);
  const [previewRule, setPreviewRule] = React.useState<KittingRule | null>(null);
  const [deletingRule, setDeletingRule] = React.useState<KittingRule | null>(null);
  const [selectedAction, setSelectedAction] = React.useState<KittingAction | null>(null);
  const [savedMessage, setSavedMessage] = React.useState<string | null>(null);

  // Today, not the last 7 days: the history is checked to see what the job just
  // did, and a week of rows buries today's handful. The other presets are one
  // click away.
  const [presetDays, setPresetDays] = React.useState(0);
  const [outcome, setOutcome] = React.useState('');
  const [storeFilter, setStoreFilter] = React.useState('');
  const [rows, setRows] = React.useState<KittingAction[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState<string | null>(null);

  // History lives on the Overview tab only; store tabs are rules management.
  const onStoreTab = tab !== OVERVIEW;

  const loadSettings = React.useCallback(async () => {
    const effective = await service.getEffectiveSettings();
    if (!effective) {
      setSettingsError('Failed to load settings — check that the API is reachable.');
      return;
    }
    setSettingsError(null);
    setSettings(effective);
  }, [service]);

  React.useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Token health drives the per-store banner; a failure here is not fatal to
  // the page, so it degrades to no banner rather than an error.
  const loadCredentials = React.useCallback(async () => {
    const result = await service.getCredentials();
    if (result) setCredentials(result);
  }, [service]);

  React.useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  // Monotonic id — a superseded response is dropped rather than rendered.
  const fetchIdRef = React.useRef(0);
  const fetchHistory = React.useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setHistoryError(null);
    const result = await service.getActions({
      startDate: dayjs().subtract(presetDays, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      ...(outcome ? { outcome } : {}),
      ...(storeFilter ? { store: storeFilter } : {}),
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    });
    if (fetchId !== fetchIdRef.current) return;
    if (result) {
      setRows(result.rows);
      setTotal(result.total);
    } else {
      setHistoryError('Failed to load history — check that the API is reachable.');
    }
    setLoading(false);
  }, [service, presetDays, outcome, storeFilter, page]);

  React.useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // All rule edits persist the FULL rules set (the stored `rules` key is
  // replaced wholesale), then re-read the effective settings so the list shows
  // exactly what the job will use.
  const saveRules = async (nextRules: KittingRule[], message: string) => {
    setSavingRules(true);
    const updated = await settingsService.update(ORDER_KITTING_NAMESPACE, {
      rules: rulesToStored(nextRules),
    });
    setSavingRules(false);
    if (!updated) {
      setSettingsError('Failed to save rules');
      return false;
    }
    setSavedMessage(message);
    await loadSettings();
    return true;
  };

  const rules = settings?.rules ?? [];
  const tabRules = onStoreTab ? rules.filter(rule => rule.store === tab) : rules;
  const storeCredentials = onStoreTab
    ? credentials?.stores.find(entry => entry.store === tab) ?? null
    : null;

  const historyControls = (
    <Stack direction="row" spacing={2} alignItems="center">
      <Typography variant="h6">History</Typography>
      <ButtonGroup size="small">
        {PRESETS.map(preset => (
          <Button
            key={preset.label}
            variant={presetDays === preset.days ? 'contained' : 'outlined'}
            onClick={() => {
              setPresetDays(preset.days);
              setPage(0);
            }}
          >
            {preset.label}
          </Button>
        ))}
      </ButtonGroup>
      <Select
        size="small"
        displayEmpty
        value={outcome}
        onChange={e => {
          setOutcome(e.target.value);
          setPage(0);
        }}
      >
        <MenuItem value="">All outcomes</MenuItem>
        {Object.values(OUTCOMES).map(value => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </Select>
      <Select
        size="small"
        displayEmpty
        value={storeFilter}
        onChange={e => {
          setStoreFilter(e.target.value);
          setPage(0);
        }}
      >
        <MenuItem value="">All stores</MenuItem>
        {STORES.map(value => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </Select>
      <Button size="small" variant="outlined" onClick={fetchHistory}>
        Refresh
      </Button>
      <Typography variant="body2" color="text.secondary">
        {total} action(s)
      </Typography>
    </Stack>
  );

  return (
    <Stack spacing={2} sx={{ p: 3 }}>
      {/* space-between rather than ml:'auto' on the button group — MUI Stack's
          own spacing rule sets margin-left on later children and wins on
          specificity, which left the buttons hugging the title. */}
      <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between">
        <Typography variant="h5">Order Kitting</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<VpnKeyOutlinedIcon />}
            onClick={() => setSetupOpen(true)}
          >
            Setup &amp; credentials
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setModeOpen(true)}
          >
            Settings
          </Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Replaces component line items (e.g. 2 × standard pillow) with their
        pre-kitted bundle SKU on fresh Dosaze orders (any storefront with an
        enabled rule), inside ShipBob&apos;s 60-minute import delay — one box
        instead of two. Customers are never emailed and the order total never
        changes. In dry-run, actions below are what the job <em>would</em> have
        done.
      </Typography>

      {settingsError && <Alert severity="error">{settingsError}</Alert>}

      <Tabs
        value={tab}
        onChange={(_, value) => {
          setTab(value);
          setPage(0);
        }}
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Overview" value={OVERVIEW} />
        {STORES.map(store => {
          const status = storeStatus(settings, store);
          return (
            <Tab
              key={store}
              value={store}
              label={
                // The tooltip wraps ONLY the dot: wrapping the whole label
                // makes the tab's accessible name the status ("Dry-run") for
                // every store instead of the store name.
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{store}</span>
                  <Tooltip title={status.label}>
                    <Box
                      aria-label={status.label}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: status.color,
                      }}
                    />
                  </Tooltip>
                </Stack>
              }
            />
          );
        })}
      </Tabs>

      {onStoreTab && storeCredentials && !storeCredentials.canKitLive && (
        <Alert
          severity={storeCredentials.canEvaluate ? 'info' : 'error'}
          action={
            <Button size="small" onClick={() => setSetupOpen(true)}>
              Setup
            </Button>
          }
        >
          {storeCredentials.canEvaluate
            ? `${tab} has no working order-edit token, so it can only ever dry-run — rules are evaluated and logged, but no order is changed.`
            : `${tab}'s Shopify read token is failing, so this store cannot be evaluated at all.`}
        </Alert>
      )}

      {onStoreTab && (
        <RulesTable
          rules={tabRules}
          saving={savingRules}
          onToggle={(rule, enabled) =>
            saveRules(
              rules.map(r => (r.ruleId === rule.ruleId ? { ...r, enabled } : r)),
              `Rule ${rule.ruleId} ${enabled ? 'enabled' : 'disabled'}`,
            )
          }
          onAdd={() => {
            setEditingRule(null);
            setRuleDialogOpen(true);
          }}
          onEdit={rule => {
            setEditingRule(rule);
            setRuleDialogOpen(true);
          }}
          onDelete={rule => setDeletingRule(rule)}
          onPreview={rule => setPreviewRule(rule)}
        />
      )}

      {!onStoreTab && (
        <>
          {historyControls}
          {historyError && <Alert severity="error">{historyError}</Alert>}
          <HistoryTable
            rows={rows}
            total={total}
            loading={loading}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            onRowClick={setSelectedAction}
          />
        </>
      )}

      <ModeDialog
        open={modeOpen}
        settings={settings}
        onClose={() => setModeOpen(false)}
        onSaved={() => {
          setSavedMessage('Settings saved — they apply from the next run.');
          loadSettings();
        }}
      />
      <RuleDialog
        open={ruleDialogOpen}
        rule={editingRule}
        fixedStore={onStoreTab ? tab : undefined}
        existingRuleIds={rules.map(r => r.ruleId)}
        saving={savingRules}
        onClose={() => setRuleDialogOpen(false)}
        onSave={async rule => {
          const next = editingRule
            ? rules.map(r => (r.ruleId === rule.ruleId ? rule : r))
            : [...rules, rule];
          const ok = await saveRules(next, `Rule ${rule.ruleId} saved`);
          if (ok) setRuleDialogOpen(false);
        }}
      />
      <OrderDetailDrawer
        action={selectedAction}
        open={selectedAction != null}
        onClose={() => setSelectedAction(null)}
      />
      <DeleteRuleDialog
        rule={deletingRule}
        deleting={savingRules}
        onCancel={() => setDeletingRule(null)}
        onConfirm={async () => {
          const target = deletingRule;
          if (!target) return;
          const ok = await saveRules(
            rules.filter(r => r.ruleId !== target.ruleId),
            `Rule ${target.ruleId} deleted`,
          );
          if (ok) setDeletingRule(null);
        }}
      />
      <SetupDialog
        open={setupOpen}
        onClose={() => {
          setSetupOpen(false);
          loadCredentials();
        }}
      />
      <PreviewDialog
        open={previewRule != null}
        rule={previewRule}
        store={previewRule?.store ?? (onStoreTab ? tab : 'US')}
        onClose={() => setPreviewRule(null)}
      />
      <Snackbar
        open={savedMessage != null}
        autoHideDuration={5000}
        onClose={() => setSavedMessage(null)}
      >
        <Alert severity="success">{savedMessage}</Alert>
      </Snackbar>
    </Stack>
  );
}
